import { IPDefaultButton, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import UserPicker from 'components/Pickers/UserPicker';
import { globalStackTokensGapSmall, globalTextStylesBold, leftArrowIcon, taskStartIcon } from 'globalStyles';
import { getTheme, mergeStyles, keyframes } from '@fluentui/react';
import Task from 'models/tasks/task';
import User from 'models/user';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FollowUpTemplateList } from './FollowUpTemplateList';
import { useFollowUpTemplates } from './useFollowUpTemplates';

const slideInAnimation = keyframes({
  from: { maxHeight: 0, opacity: 0 },
  to: { maxHeight: '200px', opacity: 1 },
});

const slideInClassName = mergeStyles({
  overflow: 'hidden',
  animationName: slideInAnimation,
  animationDuration: '0.2s',
  animationTimingFunction: 'ease-out',
});

export interface ITaskFollowUpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Task) => void;
  onSkip: (notifyUserId?: string) => void;
}

export const TaskFollowUpDialog = (props: ITaskFollowUpDialogProps) => {
  const { t } = useTranslation(['task', 'translation']);
  const theme = getTheme();

  const { templateItems, isLoading: isLoadingTemplates, loadTemplates } = useFollowUpTemplates();
  const [showSkipAssign, setShowSkipAssign] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.isOpen) {
      loadTemplates();
      setShowSkipAssign(false);
      setSelectedUserId(undefined);
    }
  }, [props.isOpen, loadTemplates]);

  const getFooterAction = (): JSX.Element => {
    if (!showSkipAssign) {
      return (
        <IPDefaultButton
          iconProps={taskStartIcon}
          text={t('task:FollowUp.SkipForNow')}
          onClick={() => setShowSkipAssign(true)}
        />
      );
    }

    return (
      <IPDefaultButton
        iconProps={leftArrowIcon}
        text={t('translation:General.Button.Back')}
        onClick={() => {
          setShowSkipAssign(false);
          setSelectedUserId(undefined);
        }}
      />
    );
  };

  const renderSkipSection = () => {
    return (
      <div className={slideInClassName}>
        <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { paddingTop: 4, paddingBottom: 8 } }}>
          <IPText variant="medium" styles={globalTextStylesBold}>
            {t('task:FollowUp.SkipForNow')}
          </IPText>
          <IPText styles={{ root: { color: theme.palette.neutralSecondary } }}>
            {t('task:FollowUp.SkipAssignUser')}
          </IPText>
          <UserPicker
            users={[]}
            selectedItemId={selectedUserId}
            onSelect={(item: User | undefined) => {
              setSelectedUserId(item?.id);
            }}
          />
        </IPStack>
      </div>
    );
  };

  return (
    <GenericModal
      isOpen={props.isOpen}
      title={t('task:TopTasks.FollowUp')}
      subTitle={t('task:FollowUp.Subtitle')}
      onClose={props.onClose}
      onSave={showSkipAssign ? () => props.onSkip(selectedUserId) : undefined}
      saveButtonText={t('task:FollowUp.SkipConfirmButton')}
      closeButtonText={t('translation:General.Button.Cancel')}
      optionalButtons={getFooterAction()}
      maxHeight="82vh"
      maxWidth={560}
      minWidth={480}
    >
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack.Item>
          {!showSkipAssign && (
            <IPStack tokens={globalStackTokensGapSmall}>
              <IPText variant="medium" styles={globalTextStylesBold}>
                {t('task:FollowUp.ChooseTemplate')}
              </IPText>
              <IPStack.Item
                styles={{
                  root: {
                    border: `1px solid ${theme.palette.neutralLight}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                  },
                }}
              >
                <FollowUpTemplateList
                  templateItems={templateItems}
                  isLoading={isLoadingTemplates}
                  onSelect={(template) => props.onSave(template)}
                  maxHeight={360}
                />
              </IPStack.Item>
            </IPStack>
          )}
          {showSkipAssign && renderSkipSection()}
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};
